import moment from "moment";
import { CTePessoaDto } from "../CTe.Dto";

export interface NFeParticipanteXml extends CTePessoaDto {
  origem?: string;
}

export interface NFeXmlItem {
  id: string;
  nomeArquivo: string;
  chave: string;
  numero: string;
  serie: string;
  dataEmissao: string;
  emitente: CTePessoaDto;
  destinatario: CTePessoaDto;
  valorProdutos: number;
  valorTotalNF: number;
  pesoLiquido: number;
  pesoBruto: number;
  quantidadeVolumes: number;
  especieVolumes: string;
  importarNFe: boolean;
  importarChave: boolean;
}

export const formatarDataEmissao = (dataStr: string): string => {
  if (!dataStr) return "";
  const trimmed = dataStr.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }
  const m = moment(trimmed);
  if (m.isValid()) {
    return m.format("DD/MM/YYYY");
  }
  const partes = trimmed.split("T")[0].split("-");
  if (partes.length === 3 && partes[0].length === 4) {
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }
  return trimmed;
};

const getTagContent = (parent: Element | null | undefined, tagName: string): string => {
  if (!parent) return "";
  const el = parent.getElementsByTagName(tagName)[0];
  return el?.textContent?.trim() || "";
};

export const parseNFeXml = (xmlText: string, fileName: string = "nfe.xml"): NFeXmlItem | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");

    // Verificar se houve erro de parse
    const parserError = xmlDoc.getElementsByTagName("parsererror")[0];
    if (parserError) {
      console.warn("Erro ao fazer parse do XML:", parserError.textContent);
      return null;
    }

    // Chave de acesso
    let chave = getTagContent(xmlDoc.documentElement, "chNFe");
    const infNFe = xmlDoc.getElementsByTagName("infNFe")[0];
    if (!chave && infNFe) {
      const idAttr = infNFe.getAttribute("Id");
      if (idAttr && idAttr.startsWith("NFe")) {
        chave = idAttr.substring(3);
      } else if (idAttr) {
        chave = idAttr;
      }
    }

    // Identificação da NF-e
    const ide = xmlDoc.getElementsByTagName("ide")[0];
    const numero = getTagContent(ide, "nNF") || "0";
    const serie = getTagContent(ide, "serie") || "1";
    const rawDataEmissao =
      getTagContent(ide, "dhEmi") ||
      getTagContent(ide, "dEmi") ||
      getTagContent(ide, "dhSaiEnt") ||
      getTagContent(ide, "dSaiEnt") ||
      "";
    const dataEmissao = formatarDataEmissao(rawDataEmissao);

    // Emitente
    const emit = xmlDoc.getElementsByTagName("emit")[0];
    const enderEmit = emit?.getElementsByTagName("enderEmit")[0];
    const emitDoc = getTagContent(emit, "CNPJ") || getTagContent(emit, "CPF");

    const emitente: CTePessoaDto = {
      documento: emitDoc,
      nome: getTagContent(emit, "xNome"),
      nomeFantasia: getTagContent(emit, "xFant") || getTagContent(emit, "xNome"),
      inscricaoEstadual: getTagContent(emit, "IE"),
      fone: getTagContent(enderEmit, "fone"),
      endereco: getTagContent(enderEmit, "xLgr"),
      enderecoNumero: getTagContent(enderEmit, "nro"),
      bairro: getTagContent(enderEmit, "xBairro"),
      codigoMunicipio: getTagContent(enderEmit, "cMun"),
      nomeMunicipio: getTagContent(enderEmit, "xMun"),
      uf: getTagContent(enderEmit, "UF"),
      cep: getTagContent(enderEmit, "CEP"),
      codigoPais: getTagContent(enderEmit, "cPais") || "1058",
      nomePais: getTagContent(enderEmit, "xPais") || "BRASIL",
    };

    // Destinatário
    const dest = xmlDoc.getElementsByTagName("dest")[0];
    const enderDest = dest?.getElementsByTagName("enderDest")[0];
    const destDoc = getTagContent(dest, "CNPJ") || getTagContent(dest, "CPF");
    const indIEDest = getTagContent(dest, "indIEDest");

    const destinatario: CTePessoaDto = {
      documento: destDoc,
      nome: getTagContent(dest, "xNome"),
      nomeFantasia: getTagContent(dest, "xFant") || getTagContent(dest, "xNome"),
      inscricaoEstadual: getTagContent(dest, "IE"),
      indicadorIETomador: indIEDest ? Number(indIEDest) : 1,
      fone: getTagContent(enderDest, "fone"),
      endereco: getTagContent(enderDest, "xLgr"),
      enderecoNumero: getTagContent(enderDest, "nro"),
      bairro: getTagContent(enderDest, "xBairro"),
      codigoMunicipio: getTagContent(enderDest, "cMun"),
      nomeMunicipio: getTagContent(enderDest, "xMun"),
      uf: getTagContent(enderDest, "UF"),
      cep: getTagContent(enderDest, "CEP"),
      codigoPais: getTagContent(enderDest, "cPais") || "1058",
      nomePais: getTagContent(enderDest, "xPais") || "BRASIL",
    };

    // Totais
    const icmsTot = xmlDoc.getElementsByTagName("ICMSTot")[0];
    const valorProdutos = parseFloat(getTagContent(icmsTot, "vProd")) || 0;
    const valorTotalNF = parseFloat(getTagContent(icmsTot, "vNF")) || 0;

    // Transporte / Volumes
    const vol = xmlDoc.getElementsByTagName("vol")[0];
    const pesoLiquido = parseFloat(getTagContent(vol, "pesoL")) || 0;
    const pesoBruto = parseFloat(getTagContent(vol, "pesoB")) || 0;
    const quantidadeVolumes = parseFloat(getTagContent(vol, "qVol")) || 0;
    const especieVolumes = getTagContent(vol, "esp") || "";

    if (!chave && !numero && !emitDoc && !destDoc) {
      // Não é um XML de NF-e válido
      return null;
    }

    return {
      id: chave || `${numero}-${serie}-${Date.now()}-${Math.random()}`,
      nomeArquivo: fileName,
      chave,
      numero,
      serie,
      dataEmissao,
      emitente,
      destinatario,
      valorProdutos,
      valorTotalNF,
      pesoLiquido,
      pesoBruto,
      quantidadeVolumes,
      especieVolumes,
      importarNFe: true,
      importarChave: true,
    };
  } catch (err) {
    console.error("Erro ao processar XML da NF-e:", err);
    return null;
  }
};
